import { getQuarter } from "@utils/dateUtils";
import { slugify } from "@utils/string";
import { useSession } from "next-auth/react";
import { useLocale, useTranslations } from "next-intl";
import { useParams, useRouter } from "next/navigation";
import { FC, Fragment, useContext, useEffect, useState } from "react";

import AddToFavorites from "@/components/AddToFavorites/AddToFavorites";
import FloorPlan from "@/components/AvailableUnitsList/FloorPlan";
import { useNotification } from "@/components/NotificationProvider/NotificationProvider";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import { LoadingContext } from "@/context/LoaderContext";
import MagnifyContextProvider from "@/context/MagnifyContext";
import { ModalsContext } from "@/context/ModalsContext";
import { NavigationContext } from "@/context/NavigationContext";
import { ctaClickTracking, ecomInitiateCheckout } from "@/dataLayer/dataLayer";
import useMediaQuery from "@/hooks/useMediaQuery";
import useResponsiveLabel from "@/hooks/useResponsiveLabel";
import { CommercetoolsUnit } from "@/types";

import "./UnitDetails.css";
import { getAvailability } from "./action";

interface Props {
  unit: CommercetoolsUnit;
  closeHandler?: () => void;
  customClass?: string;
  totalUnits?: number;
  isBelongToCommunity?: boolean | void;
  matchedCommunity?: any;
}
const attributes = ["name", "project", "identifier", "unitType", "areaSqft", "areaSqmt", "status", "isFurnished", "balcony", "bedrooms"];

const UnitDetails: FC<Props> = ({ unit, totalUnits, customClass, isBelongToCommunity, matchedCommunity }) => {
  const t = useTranslations();
  const router = useRouter();
  const { data: session } = useSession();
  const { showUnavailableUnit } = useNotification();
  const locale = useLocale();
  const [displayGallery, setDisplayGallery] = useState<boolean>(false);
  const [displayPanorama, setDisplayPanorama] = useState<boolean>(false);
  const [displayTour, setDisplayTour] = useState<boolean>(false);
  const { loading } = useContext(LoadingContext);
  const params = useParams<any>();

  const enquireLabel = useResponsiveLabel(t("stickybar.mobile.enquire"), t("stickybar.desktop.enquire"));

  const handleReserve = async (inventoryID: string, id: string) => {
    loading(true);
    const response = await getAvailability(inventoryID, id);
    if (response) {
      const projectTitle = unit.project?.replaceAll(" ", "-").toLowerCase();
      const projectName = unit.propertyName?.replaceAll(" ", "-").toLowerCase();
      const productIdentifier = `${projectTitle ?? ""}/${projectName ?? ""}`;
      ecomInitiateCheckout({
        cta_name: t("unit.reserve.now"),
        property_name: unit.project,
        project_location: `${unit.area}, ${unit.city}`,
        property_type: unit.identifier,
        bedroom_numbers: unit.bedrooms,
        bathrooms: "no of bathrooms given", // TODO fixme when data will come
        unit_id: unit.name,
        product_identifier: productIdentifier,
        unit_type: unit.unitType,
        unit_category: unit.isFurnished ? "furnished" : "unfurnished",
        unit_size: unit.areaSqft,
        unit_price: unit.price,
        currency: unit.currency,
        handover_qaurter_year: getQuarter(unit.deliveryDate),
        status: unit.status,
        user_logged_in: !!session,
        // user_id: session?.user.id || "",
      });
      router.push(`/${locale}/checkout?step=contact`, { scroll: true });
    } else {
      loading(false);
      ctaClickTracking("reserve", "unit page", "property unavailable");
      showUnavailableUnit(undefined);
      // showNotification(t("reservation.label.unitUnavailable"), 5000, NotificationType.ERROR);
    }
  };

  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const { desktopPanelID } = useContext(NavigationContext);

  const share = async (shareData: any) => {
    const unitName = unit?.name.replaceAll("/", "-");
    const projectName = unit?.project;
    const bedroomsIdentifier = unit?.bedrooms + " " + unit?.identifier;
    let shareUrl;
    if (unitName && projectName) {
      // shareUrl = `/${locale}/projects/${slugify(projectName)}/properties/${slugify(bedroomsIdentifier)}/${unitName}/`;
      shareUrl =
        isBelongToCommunity && matchedCommunity
          ? `/${locale}/communities/${matchedCommunity.slug}/projects/${slugify(projectName)}/properties/${slugify(bedroomsIdentifier)}/${unitName}/`
          : `/${locale}/projects/${slugify(projectName)}/properties/${slugify(bedroomsIdentifier)}/${unitName}/`;
    }

    if (navigator.share) {
      shareData.url = shareUrl;
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.error(err);
      }
    } else {
      navigator.clipboard.writeText(shareData.url);
      alert(t("copy.link"));
      console.log("share not supported");
    }
  };

  useEffect(() => {
    const pathName = params?.unit ?? "";
    const unitId = document.querySelector(`div[unit-name='${pathName}']`)?.getAttribute("unit-id");
    const targetElement = document.getElementById(`accordion__heading-${unitId}`);
    const accordionPanel = document.getElementById(`accordion__panel-${unitId}`);

    if (targetElement && accordionPanel && (totalUnits || 0) > 0) {
      targetElement.setAttribute("aria-expanded", "true");
      accordionPanel.removeAttribute("hidden");
      if (isDesktop) {
        window.scrollTo({
          top: accordionPanel?.getBoundingClientRect()?.top - 150,
          behavior: "smooth",
        });
      }
      // BUG 93492
      if (isMobile) {
        console.log("test mobile:", unitId);

        const unitButton = document.getElementById(`mobile-button-${unitId}`);
        if (unitButton) {
          window.scrollTo({
            top: unitButton.offsetTop + (window?.outerHeight - 165),
            behavior: "smooth",
          });
          unitButton.click();
        }
      }
    }
  }, [isDesktop, totalUnits, unit.id, params?.unit, isMobile]);

  return (
    <>
      <div className={customClass}>
        <div className="md:grid md:grid-cols-2 md:gap-4xl grow px-m flex flex-col gap-xl md:px-[56px] md:py-l">
          <div>
            <div className="hidden md:block">
              <h3 className="font-primary font-medium text-xs uppercase mb-l">{t("aul.unitDetails")}</h3>
            </div>
            <div>
              {attributes.map((attribute: string, index) => {
                return !!unit[attribute as keyof CommercetoolsUnit] && ((isDesktop && index != 0) || !isDesktop) ? (
                  <div className="even:bg-gray-7 flex justify-between gap-s" key={`aul-attribute-${attribute}`}>
                    <div data-attribute={attribute} className="font-secondary text-s py-s px-xxs md:px-0">
                      {t(`aul.unit.${attribute}`)}
                    </div>
                    <div className="uppercase font-primary font-medium text-right text-xs  py-s px-xxs md:px-0">{`${["areaSqft", "areaSqft"].includes(attribute) ? Math.round(parseFloat(unit[attribute as keyof CommercetoolsUnit] as string)) : unit[attribute as keyof CommercetoolsUnit]}`}</div>
                  </div>
                ) : (
                  <Fragment key={`aul-attribute-${attribute}`} />
                );
              })}
            </div>
          </div>
          {/* {propertyType !== "Villa" && ( */}
          <div>
            <MagnifyContextProvider>
              <FloorPlan unit={unit} />
            </MagnifyContextProvider>
          </div>
          {/* )} */}
        </div>
        <div className="flex flex-col gap-m item-center justify-center md:py-m border-t border-t-gray-10 md:flex-row md:items-center md:justify-end bg-white md:bg-transparent sticky bottom-0 pb-l md:static stickyBarMobile px-m md:px-[56px]">
          {isDesktop && desktopPanelID === "select-unit" ? (
            <div className="text-center">{isDesktop && <AddToFavorites item={unit} cta={true} />}</div>
          ) : (
            <>
              <div className="text-center flex items-center justify-center mt-s md:mt-0 gap-xs share-button">
                <button
                  onClick={() => share({ title: t("title.common.shareUnit"), text: unit.name, url: "" })}
                  className="underline text-s font-medium"
                >
                  {t("common.label.shareUnit")}
                </button>
                <AddToFavorites item={unit} className="relative z-9  ml-auto hidden md:flex sm:hidden" fillColor="fill-black" />
                {/* <Button
              rounded
              size={ButtonSize.SMALL}
              type="button"
              className="normal-case font-secondary font-medium text-s underline underline-offset-4"
            >
              {t("unit.showOnMap")}
            </Button> */}
                {/* {unit?.virtualTourUrl && (
              <Button
                rounded
                size={ButtonSize.SMALL}
                theme={ButtonTheme.BLACK}
                type="button"
                variant={ButtonVariant.TERTIARY}
                customClass="normal-case font-secondary"
                onClick={() => {
                  setDisplayTour(true);
                  ctaClickTracking(t("unit.3d"), "UnitDetails", "UnitDetails");
                }}
              >
                {t("unit.3d")}
              </Button>
            )} */}
                {/* {unit.panorama?.length && (
              <Button
                rounded
                size={ButtonSize.SMALL}
                theme={ButtonTheme.BLACK}
                type="button"
                variant={ButtonVariant.TERTIARY}
                customClass="normal-case font-secondary md:mr-xl"
                onClick={() => {
                  setDisplayPanorama(true);
                }}
              >
                {t("unit.viewPanorama")}
              </Button>
            )} */}
              </div>
              <div className="text-center">
                <Button
                  rounded
                  size={ButtonSize.MEDIUM}
                  theme={ButtonTheme.BLACK}
                  type="button"
                  variant={ButtonVariant.PRIMARY}
                  customClass="md:!px-xl w-[calc(100%-32px)]  mx-auto md:w-auto"
                  onClick={() => {
                    if (unit.sellable) {
                      handleReserve(unit.inventoryID, unit.id);
                    }
                  }}
                >
                  {unit.sellable ? t("unit.reserve.now") : enquireLabel}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
      {/* {displayTour && (
        <UnitModal
          title={t("aul.modal.3dAndMedia")}
          closeHandler={() => {
            setDisplayTour(false);
          }}
          key={`unit-${unit.id}-tour`}
        >
          <p>Tour modal</p>
        </UnitModal>
      )}
      {displayGallery && (
        <UnitModal
          title={t("aul.modal.3dAndMedia")}
          closeHandler={() => {
            setDisplayTour(false);
          }}
          key={`unit-${unit.id}-tour`}
        >
          <p>Gallery modal</p>
        </UnitModal>
      )}
      {displayPanorama && (
        <UnitModal
          title={t("aul.modal.3dAndMedia")}
          closeHandler={() => {
            setDisplayTour(false);
          }}
          key={`unit-${unit.id}-tour`}
        >
          <p>Panorama modal</p>
        </UnitModal>
      )} */}
    </>
  );
};
export default UnitDetails;
